import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { TransactionDialog } from "@/components/TransactionDialog";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { AlertTriangle, ArrowRightLeft, Receipt, X } from "lucide-react";

export function DelistedAlert() {
    const qc = useQueryClient();
    const [sellFor, setSellFor] = useState(null);
    const [rollFor, setRollFor] = useState(null);
    const [roll, setRoll] = useState({ new_ticker: "", new_shares: "", date: new Date().toISOString().slice(0, 10) });

    const { data } = useQuery({
        queryKey: ["delisted"],
        queryFn: async () => (await api.get("/corporate-actions/delisted")).data,
    });
    const items = data?.delisted || [];

    const refreshAll = () => qc.invalidateQueries();

    const ignore = async (tk) => {
        try { await api.post("/corporate-actions/delisted/ack", { ticker: tk }); refreshAll(); }
        catch (e) { toast.error(apiErr(e)); }
    };

    const openRoll = (d) => {
        setRoll({ new_ticker: "", new_shares: "", date: new Date().toISOString().slice(0, 10) });
        setRollFor(d);
    };

    const submitRoll = async () => {
        try {
            const res = await api.post("/corporate-actions/rollover", {
                old_ticker: rollFor.ticker, new_ticker: roll.new_ticker,
                new_shares: parseFloat(roll.new_shares), date: roll.date,
            });
            toast.success(`Rolled ${rollFor.ticker} into ${roll.new_ticker.toUpperCase()}.`);
            setRollFor(null);
            refreshAll();
            return res;
        } catch (e) { toast.error(apiErr(e)); }
    };

    if (items.length === 0) return null;

    return (
        <>
            <Card className="p-4 border-l-4 border-l-loss bg-loss/5" data-testid="delisted-alert">
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-loss/15 p-1.5"><AlertTriangle className="w-4 h-4 text-loss" /></div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-sm font-semibold">No live price for {items.length} holding{items.length > 1 ? "s" : ""}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">The market stopped returning data for {items.length > 1 ? "these tickers" : "this ticker"} — usually a merger or delisting. We've stopped re-checking {items.length > 1 ? "them" : "it"}. Choose how to record the event:</p>
                        <div className="mt-3 space-y-2.5">
                            {items.map((d) => (
                                <div key={d.ticker} className="flex items-center justify-between gap-3 flex-wrap border-t border-border/50 pt-2.5" data-testid={`delisted-row-${d.ticker}`}>
                                    <div className="text-sm">
                                        <b className="num">{d.ticker}</b> <span className="text-muted-foreground">{d.name}</span>
                                        <span className="text-muted-foreground"> · {fmtNumber(d.shares, 4)} sh · cost {fmtCurrency(d.cost_basis)}{d.last_price != null ? ` · last ${fmtCurrency(d.last_price, { decimals: 2 })}` : ""}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setSellFor(d)} data-testid={`sell-delisted-${d.ticker}`}><Receipt className="w-3 h-3" /> Record as sale</Button>
                                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openRoll(d)} data-testid={`roll-delisted-${d.ticker}`}><ArrowRightLeft className="w-3 h-3" /> Roll into new ticker</Button>
                                        <button onClick={() => ignore(d.ticker)} className="text-muted-foreground hover:text-foreground text-xs inline-flex items-center gap-0.5" title="Ignore" data-testid={`ignore-delisted-${d.ticker}`}><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Card>

            <TransactionDialog
                open={!!sellFor}
                onOpenChange={(o) => !o && setSellFor(null)}
                editing={null}
                prefill={sellFor ? { transaction_type: "SELL", ticker: sellFor.ticker, sub_portfolio: sellFor.subs?.[0]?.sub, quantity: sellFor.shares, price: sellFor.last_price ?? "" } : null}
                onSaved={() => { setSellFor(null); refreshAll(); }}
            />

            <Dialog open={!!rollFor} onOpenChange={(o) => !o && setRollFor(null)}>
                <DialogContent className="max-w-md" data-testid="rollover-dialog">
                    <DialogHeader>
                        <DialogTitle>Roll {rollFor?.ticker} into a new ticker</DialogTitle>
                        <DialogDescription>
                            For a stock-for-stock merger. This closes your {rollFor?.ticker} position and opens the new one, carrying your cost basis of {rollFor ? fmtCurrency(rollFor.cost_basis) : ""} across (no artificial gain). Record it as a sale instead if you received cash.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>New ticker</Label>
                            <Input value={roll.new_ticker} onChange={(e) => setRoll({ ...roll, new_ticker: e.target.value.toUpperCase() })} placeholder="e.g. AMCX" data-testid="roll-new-ticker" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>New shares received</Label>
                            <Input type="number" step="0.0001" value={roll.new_shares} onChange={(e) => setRoll({ ...roll, new_shares: e.target.value })} placeholder="0" data-testid="roll-new-shares" className="num" />
                        </div>
                        <div className="space-y-1.5 col-span-2">
                            <Label>Effective date</Label>
                            <Input type="date" value={roll.date} onChange={(e) => setRoll({ ...roll, date: e.target.value })} data-testid="roll-date" className="num" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={submitRoll} disabled={!roll.new_ticker || !roll.new_shares || !roll.date} data-testid="roll-submit">Roll over</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
